import i18n from '@/utils/i18n';
import React from 'react';
import {
  Avatar,
  Button,
  createAvatarProps,
  Icons,
  InputCompound,
  Separator,
  SidePanel,
  Switch,
  Typography,
} from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import {
  InviteEmployeeFormState,
  SetInviteEmployeeFormField,
} from '@sales-portal/domain/accounts/screens/employee-list/containers/employee-invite-drawer/types';
import { Pressable, ScrollView, View } from 'react-native';
import { SearchableCountryDropdown } from '@sales-portal/domain/accounts/screens/employee-list/containers/employee-invite-drawer/components/searchable-country-dropdown/searchable-country-dropdown';

const SWITCH_SIZE_TOKEN = '$28';
const INPUT_SIZE = '$44';
const AVATAR_PLACEHOLDER_ICON_SIZE = 16;

interface InviteEmployeeFormProps {
  canSubmit: boolean;
  form: InviteEmployeeFormState;

  inviting: boolean;
  onSubmit: () => Promise<void> | void;
  setFormField: SetInviteEmployeeFormField;
}

export const InviteEmployeeForm: React.FC<InviteEmployeeFormProps> = ({
  canSubmit,
  form,
  inviting,
  onSubmit,
  setFormField,
}) => {
  const [styles, theme] = useStyles();

  const nameLabel = i18n.t('sales_portal.employee_invite.name');
  const phoneLabel = i18n.t('sales_portal.employee_invite.mobile_number');
  const loginEmailLabel = i18n.t('sales_portal.employee_invite.login_email');
  const communicationEmailLabel = i18n.t(
    'sales_portal.employee_invite.communication_email',
  );
  const submitLabel = i18n.t('sales_portal.employee_invite.submit');

  return (
    <>
      <SidePanel.Body
        accessibilityLabel={i18n.t('sales_portal.employee_invite.title')}
        role="dialog"
      >
        <Typography accessibilityRole="header" variant="heading">
          {i18n.t('sales_portal.employee_invite.title')}
        </Typography>
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator
        >
          <View
            accessibilityElementsHidden
            importantForAccessibility="no-hide-descendants"
            style={styles.avatarWrapper}
          >
            {form.name.trim() ? (
              <Avatar
                {...createAvatarProps(form.name, '', 2)}
                backgroundColor={theme.sk.color.icon.blue}
                size="$xl"
              />
            ) : (
              <View style={styles.avatarPlaceholder}>
                <Icons.User2
                  color={theme.sk.color.icon.secondary}
                  size={AVATAR_PLACEHOLDER_ICON_SIZE}
                />
              </View>
            )}
          </View>

          <View style={styles.fields}>
            <InputCompound size={INPUT_SIZE}>
              <InputCompound.Label htmlFor="name">
                {nameLabel}
              </InputCompound.Label>
              <InputCompound.Box>
                <InputCompound.Section>
                  <InputCompound.Area
                    accessibilityLabel={nameLabel}
                    autoCapitalize="words"
                    autoComplete="name"
                    autoCorrect={false}
                    id="name"
                    onChangeText={(value) => setFormField('name', value)}
                    placeholder={i18n.t(
                      'sales_portal.employee_invite.name_placeholder',
                    )}
                    textContentType="name"
                    value={form.name}
                  />
                </InputCompound.Section>
              </InputCompound.Box>
            </InputCompound>

            <InputCompound size={INPUT_SIZE}>
              <InputCompound.Label htmlFor="phoneNumber">
                {phoneLabel}
              </InputCompound.Label>
              <InputCompound.Box>
                <InputCompound.Section>
                  <SearchableCountryDropdown
                    onSelectCountry={(code) =>
                      setFormField('countryCode', code)
                    }
                    selectedCountryCode={form.countryCode}
                  />
                  <Separator vertical />
                </InputCompound.Section>
                <InputCompound.Section>
                  <InputCompound.Area
                    accessibilityLabel={phoneLabel}
                    autoComplete="tel"
                    id="phoneNumber"
                    keyboardType="phone-pad"
                    onChangeText={(number) =>
                      setFormField('phoneNumber', number)
                    }
                    paddingLeft={12}
                    placeholder={i18n.t(
                      'sales_portal.employee_invite.mobile_number_placeholder',
                    )}
                    textContentType="telephoneNumber"
                    value={form.phoneNumber}
                  />
                </InputCompound.Section>
              </InputCompound.Box>
            </InputCompound>

            <InputCompound size={INPUT_SIZE}>
              <InputCompound.Label htmlFor="loginEmail">
                {loginEmailLabel}
              </InputCompound.Label>
              <InputCompound.Box>
                <InputCompound.Section>
                  <InputCompound.Area
                    accessibilityLabel={loginEmailLabel}
                    autoCapitalize="none"
                    autoComplete="email"
                    autoCorrect={false}
                    id="loginEmail"
                    keyboardType="email-address"
                    onChangeText={(value) => setFormField('loginEmail', value)}
                    placeholder={i18n.t(
                      'sales_portal.employee_invite.email_placeholder',
                    )}
                    textContentType="emailAddress"
                    value={form.loginEmail}
                  />
                </InputCompound.Section>
              </InputCompound.Box>
            </InputCompound>

            <InputCompound size={INPUT_SIZE}>
              <InputCompound.Label htmlFor="communicationEmail">
                {communicationEmailLabel}
              </InputCompound.Label>
              <InputCompound.Box>
                <InputCompound.Section>
                  <InputCompound.Area
                    accessibilityLabel={communicationEmailLabel}
                    autoCapitalize="none"
                    autoComplete="email"
                    autoCorrect={false}
                    id="communicationEmail"
                    keyboardType="email-address"
                    onChangeText={(value) =>
                      setFormField('communicationEmail', value)
                    }
                    placeholder={i18n.t(
                      'sales_portal.employee_invite.email_placeholder',
                    )}
                    textContentType="emailAddress"
                    value={form.communicationEmail}
                  />
                </InputCompound.Section>
              </InputCompound.Box>
            </InputCompound>

            <Pressable
              accessibilityHint={i18n.t(
                'sales_portal.employee_invite.admin_access_description',
              )}
              accessibilityLabel={i18n.t(
                'sales_portal.employee_invite.admin_access',
              )}
              accessibilityRole="switch"
              accessibilityState={{ checked: form.isAdmin }}
              onPress={() => setFormField('isAdmin', !form.isAdmin)}
              style={styles.toggleRow}
            >
              <View
                accessibilityElementsHidden
                importantForAccessibility="no-hide-descendants"
                pointerEvents="none"
              >
                <Switch checked={form.isAdmin} size={SWITCH_SIZE_TOKEN} />
              </View>
              <View style={styles.toggleLabels}>
                <Typography size="$sm" variant="label">
                  {i18n.t('sales_portal.employee_invite.admin_access')}
                </Typography>
                <Typography muted variant="caption">
                  {i18n.t(
                    'sales_portal.employee_invite.admin_access_description',
                  )}
                </Typography>
              </View>
            </Pressable>
          </View>
        </ScrollView>
      </SidePanel.Body>

      <SidePanel.Footer>
        <Button
          accessibilityLabel={submitLabel}
          disabled={!canSubmit || inviting}
          onPress={onSubmit}
          size="$md"
          variant="primary"
          width="100%"
        >
          {submitLabel}
        </Button>
      </SidePanel.Footer>
    </>
  );
};

const useStyles = createStyles((theme) => ({
  avatarPlaceholder: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.tertiary.default,
    borderRadius: theme.sk.radii.radiusRound,
    height: 80,
    justifyContent: 'center',
    width: 80,
  },
  avatarWrapper: {
    alignItems: 'flex-start',
    paddingBottom: theme.sk.spacing.spaceLarge,
  },
  fields: {
    gap: theme.sk.spacing.spaceMedium,
  },
  scrollContent: {
    paddingVertical: theme.sk.spacing.spaceMedium,
  },
  toggleLabels: {
    flex: 1,
  },
  toggleRow: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceMedium,
    paddingTop: theme.sk.spacing.spaceSmall,
  },
}));
